// Step-up MFA (FR-06): sensitive actions need a single-use token from
// /mfa/verify bound to the action's purpose. useStepUp() returns a function
// that starts a challenge (authenticator app when enrolled, else SMS), asks for
// the code in a modal and resolves the token, or null when the person cancels.
import { Button, Group, Modal, Stack, Text, TextInput } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client";
import type { paths } from "../../api/schema";
import { FormError } from "../ui/Page";
import { errorMessage } from "./errors";
import { fetchMe, ME_KEY } from "./me";

export type StepUpPurpose = paths["/mfa/challenge"]["post"]["requestBody"]["content"]["application/json"]["purpose"];
type Channel = "sms" | "totp";
export type StepUp = (purpose: StepUpPurpose) => Promise<string | null>;

const StepUpContext = createContext<StepUp | null>(null);

// Purposes a new authenticator app confirms alone (backend domain.TOTPHeld).
const HOLD_FREE: StepUpPurpose[] = ["suspend", "admin_change", "totp_enrol"];

type Pending = { id: number; purpose: StepUpPurpose; channel: Channel; phone: string; challengeId: string | null };

export function StepUpProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<Pending | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const resolver = useRef<((token: string | null) => void) | null>(null);
  const seq = useRef(0);

  const finish = useCallback((token: string | null) => {
    resolver.current?.(token);
    resolver.current = null;
    setPending(null);
    setCode("");
    setError(null);
    setBusy(false);
  }, []);

  const stepUp = useCallback<StepUp>(
    async (purpose) => {
      resolver.current?.(null); // a newer request supersedes an unfinished one
      const id = ++seq.current;
      const result = new Promise<string | null>((resolve) => {
        resolver.current = resolve;
      });
      const me = await queryClient
        .fetchQuery({ queryKey: ME_KEY, queryFn: fetchMe, staleTime: 30_000 })
        .catch(() => null);
      if (seq.current !== id) return result;
      // A new authenticator app cannot alone confirm most actions for a day
      // (SR-2026-10-28-02): a code by SMS does.
      const held = !!me?.totp_new_until && new Date(me.totp_new_until) > new Date() && !HOLD_FREE.includes(purpose);
      const channel: Channel = me?.totp_enabled && !held ? "totp" : "sms";
      setCode("");
      setError(null);
      setPending({ id, purpose, channel, phone: me?.masked_phone ?? "", challengeId: null });
      const { data, error } = await api.POST("/mfa/challenge", { body: { purpose, channel } });
      if (seq.current !== id) return result;
      if (data) setPending((p) => (p && p.id === id ? { ...p, challengeId: data.challenge_id } : p));
      else setError(errorMessage(error, t("error.generic")));
      return result;
    },
    [queryClient, t],
  );

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    if (!pending?.challengeId) return;
    setBusy(true);
    setError(null);
    const { data, error } = await api.POST("/mfa/verify", { body: { challenge_id: pending.challengeId, code } });
    setBusy(false);
    if (!data) return setError(errorMessage(error, t("error.generic")));
    finish(data.step_up_token);
  };

  const totp = pending?.channel === "totp";
  return (
    <StepUpContext.Provider value={stepUp}>
      {children}
      <Modal
        opened={pending !== null}
        onClose={() => finish(null)}
        title={t("stepup.title")}
        centered
        closeButtonProps={{ "aria-label": t("stepup.close") }}
      >
        <form onSubmit={verify} data-testid="step-up">
          <Stack gap="sm">
            <Text size="sm">{totp ? t("stepup.totp") : t("stepup.sms", { phone: pending?.phone ?? "" })}</Text>
            <TextInput
              label={totp ? t("stepup.code_totp") : t("stepup.code_sms")}
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.currentTarget.value.trim())}
              required
              data-autofocus
            />
            <FormError message={error} />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => finish(null)}>
                {t("stepup.cancel")}
              </Button>
              <Button type="submit" loading={busy} disabled={!pending?.challengeId}>
                {t("stepup.confirm")}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </StepUpContext.Provider>
  );
}

/** useStepUp returns the step-up function; await it for a token (null when cancelled). */
export function useStepUp(): StepUp {
  const s = useContext(StepUpContext);
  if (!s) throw new Error("useStepUp outside StepUpProvider");
  return s;
}
